import { z } from "zod";
import {
  CREATIVE_TEMPLATE_LAYOUT_KIND,
  CREATIVE_TEMPLATE_MAX_NODES,
  CREATIVE_TEMPLATE_SLIDE_MAX,
  CREATIVE_TEMPLATE_SLIDE_MIN,
} from "../../../utils/creativeTemplateLayout.js";
import {
  isStripWithinLimits,
  isValidContinuousSlideCount,
} from "../utils/continuousStrip.js";

const httpUrlSchema = z
  .string()
  .min(1)
  .refine((value) => !value.startsWith("data:"), {
    message: "dataUrlNotAllowed",
  });

const toStringList = (value) => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value;
  return Object.keys(value)
    .sort((left, right) => Number(left) - Number(right))
    .map((key) => value[key]);
};

const stringListSchema = z
  .union([z.array(z.string()), z.record(z.string())])
  .optional()
  .transform(toStringList);

const sourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fixed"),
    value: z.string(),
  }),
  z.object({
    kind: z.literal("asset"),
    assetRef: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal("data"),
    variable: z.string().min(1),
  }),
  z.object({
    kind: z.literal("computed"),
    expression: z.string().min(1),
  }),
  z.object({
    kind: z.literal("generated"),
    generator: z.enum(["image", "text"]).optional(),
    promptTemplate: z.string().optional(),
    negativePrompt: z.string().optional(),
    referenceAssets: stringListSchema,
    seedStrategy: z.string().optional(),
  }),
]);

const nodeLayoutSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  zIndex: z.number().optional(),
  visible: z.boolean().optional(),
  rotation: z.number().optional(),
});

const TEXT_KINDS = ["fixed", "data", "computed", "generated"];
const IMAGE_KINDS = ["asset", "data", "generated"];

const refineNodeCombos = ({ node, ctx, path }) => {
  const sourceKind = node.source?.kind;
  if (node.type === "text") {
    if (!sourceKind || !TEXT_KINDS.includes(sourceKind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "illegalSourceCombo",
        path: [...path, "source", "kind"],
      });
    }
  }
  if (node.type === "image") {
    if (!sourceKind || !IMAGE_KINDS.includes(sourceKind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "illegalSourceCombo",
        path: [...path, "source", "kind"],
      });
    }
  }
  if (node.type === "group" && sourceKind) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "illegalSourceCombo",
      path: [...path, "source", "kind"],
    });
  }
};

const nodeSchema = z.lazy(() =>
  z
    .object({
      id: z.string().min(1),
      name: z.string().min(1),
      type: z.enum(["text", "image", "shape", "group"]),
      source: sourceSchema.optional(),
      style: z
        .object({
          fontAssetRef: z.string().optional(),
          fontFaceRef: z.string().optional(),
          fontSize: z.number().optional(),
          fontWeight: z.number().optional(),
          lineHeight: z.number().optional(),
          letterSpacing: z.number().optional(),
          strokeWidth: z.number().optional(),
          strokeColor: z.string().optional(),
          shadowColor: z.string().optional(),
          shadowAngle: z.number().optional(),
          shadowDistance: z.number().optional(),
          shadowBlur: z.number().optional(),
          backgroundPadding: z.number().optional(),
          backgroundRadius: z.number().optional(),
          backgroundColor: z.string().optional(),
          color: z.string().optional(),
          align: z.enum(["left", "center", "right"]).optional(),
          orientation: z
            .enum(["horizontal", "vertical", "vertical-up"])
            .optional(),
          textStyle: z
            .enum(["normal", "italic", "underline", "italic_underline"])
            .optional()
            .catch(undefined),
          transform: z.string().optional(),
          highlights: z
            .array(
              z.object({
                id: z.string().min(1),
                kind: z.enum(["fill", "stroke", "background"]).optional(),
                color: z.string().optional(),
                strokeColor: z.string().optional(),
                strokeWidth: z.number().optional(),
                backgroundColor: z.string().optional(),
                backgroundPadding: z.number().optional(),
                backgroundRadius: z.number().optional(),
                fontAssetRef: z.string().optional(),
                fontFaceRef: z.string().optional(),
                fontWeight: z.number().optional(),
                textStyle: z.string().optional(),
                example: z.string().optional(),
              })
            )
            .optional()
            .catch(undefined),
          inlineReplacements: z
            .array(
              z.object({
                id: z.string().min(1),
                phrase: z.string().optional(),
                assetRef: z.string().optional(),
              })
            )
            .optional()
            .catch(undefined),
        })
        .optional(),
      overflow: z
        .object({
          strategy: z.enum(["shrink", "reject"]).optional(),
          minimumFontSize: z.number().optional(),
          minimumLines: z.number().optional(),
          maximumLines: z.number().optional(),
          onFailure: z.string().optional(),
        })
        .optional(),
      shape: z
        .object({
          kind: z.literal("roundedRectangle"),
          radius: z.number().optional(),
          fill: z.string().optional(),
        })
        .optional(),
      fit: z.enum(["cover", "contain", "fill"]).optional(),
      crop: z
        .object({
          x: z.number().min(0).max(1),
          y: z.number().min(0).max(1),
          width: z.number().gt(0).max(1),
          height: z.number().gt(0).max(1),
        })
        .optional(),
      constraints: z
        .object({
          minimumWidth: z.number().optional(),
          safeArea: z.number().optional(),
          lockAspectRatio: z.boolean().optional(),
          assetIntegrity: z.boolean().optional(),
          allowRecolor: z.boolean().optional(),
          protectedByNodes: stringListSchema,
        })
        .optional(),
      children: z.record(z.lazy(() => nodeSchema)).optional(),
      childOrder: stringListSchema,
      componentFrame: z
        .object({
          width: z.number().positive(),
          height: z.number().positive(),
        })
        .optional(),
      clipContent: z.boolean().optional(),
      isBackground: z.boolean().optional(),
      locked: z.boolean().optional(),
      postprocess: z.boolean().optional(),
      originAssetRef: z.string().min(1).optional(),
      originText: z.string().min(1).optional(),
      layout: nodeLayoutSchema.optional(),
    })
    .superRefine((node, ctx) => {
      refineNodeCombos({ node, ctx, path: [] });
    })
);

const backdropAssetSchema = z.object({
  type: z.literal("image"),
  assetID: z.string().min(1),
  mimeType: z.string().min(1),
  url: httpUrlSchema,
  width: z.number().positive(),
  height: z.number().positive(),
  role: z.literal("editorBackdrop"),
});

const composeAssetSchema = z.object({
  type: z.enum(["image", "font"]),
  assetID: z.string().min(1),
  mimeType: z.string().min(1),
  url: httpUrlSchema.optional(),
  version: z.number().optional(),
  checksum: z.string().optional(),
  immutable: z.boolean().optional(),
});

const variableSchema = z.object({
  label: z.string().min(1),
  type: z.enum(["string", "number", "boolean", "image", "color", "date"]),
  required: z.boolean().optional(),
  editable: z.boolean().optional(),
  constraints: z.record(z.any()).optional(),
  resolution: z.record(z.any()).optional(),
});

const artboardEntrySchema = z.object({
  label: z.string().optional(),
  width: z.number().positive(),
  height: z.number().positive(),
  referenceAssetRef: z.string().min(1).optional(),
  slideGuides: z.record(z.string().min(1)).optional(),
  nodes: z.record(nodeSchema).optional(),
  nodeLayouts: z.record(nodeLayoutSchema).default({}),
});

const artboardMapSchema = z.record(artboardEntrySchema);

const artboardRecordSchema = artboardMapSchema.refine(
  (map) => Object.keys(map || {}).length >= 1,
  {
    message: "artboardRequired",
  }
);

const slideSchema = z.object({
  id: z.string().min(1),
  artboards: artboardRecordSchema,
});

const countNodes = ({ nodes = {} }) =>
  Object.values(nodes).reduce((total, node) => {
    return total + 1 + countNodes({ nodes: node.children || {} });
  }, 0);

const collectNodeIds = ({ nodes = {}, acc = [] }) => {
  Object.values(nodes).forEach((node) => {
    acc.push(node.id);
    collectNodeIds({ nodes: node.children || {}, acc });
  });
  return acc;
};

const refineArtboardMap = ({ artboards, sharedNodes, assets, ctx, path }) => {
  Object.entries(artboards || {}).forEach(([artboardID, artboard]) => {
    const artboardNodes =
      artboard?.nodes != null ? artboard.nodes : sharedNodes;
    if (countNodes({ nodes: artboardNodes }) > CREATIVE_TEMPLATE_MAX_NODES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "nodeCapExceeded",
        path: [...path, artboardID, "nodes"],
      });
    }
    Object.keys(artboard?.nodeLayouts || {}).forEach((layoutID) => {
      if (!artboardNodes[layoutID]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "orphanLayout",
          path: [...path, artboardID, "nodeLayouts", layoutID],
        });
      }
    });
    const referenceRef = artboard?.referenceAssetRef;
    if (referenceRef && !assets?.[referenceRef]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "orphanReference",
        path: [...path, artboardID, "referenceAssetRef"],
      });
    }
    Object.entries(artboard?.slideGuides || {}).forEach(([slideID, ref]) => {
      if (!assets?.[ref]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "orphanReference",
          path: [...path, artboardID, "slideGuides", slideID],
        });
      }
    });
    const artboardIds = collectNodeIds({ nodes: artboardNodes });
    artboardIds.forEach((nodeID, index, allIds) => {
      if (allIds.indexOf(nodeID) !== index) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "duplicateNodeId",
          path: [...path, artboardID, "nodes"],
        });
      }
    });
    Object.keys(artboardNodes).forEach((rootID) => {
      if (artboardNodes[rootID]?.layout) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "rootLayoutOnNode",
          path: [...path, artboardID, "nodes", rootID, "layout"],
        });
      }
    });
  });
};

const refineContinuousStrips = ({ doc, ctx }) => {
  const slideCount = doc.layoutConfigurations?.continuousCarousel?.slideCount;
  const configPath = [
    "layoutConfigurations",
    "continuousCarousel",
    "slideCount",
  ];
  if (!isValidContinuousSlideCount({ slideCount })) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "slideBounds",
      path: configPath,
    });
    return;
  }
  Object.entries(doc.artboards || {}).forEach(([artboardID, artboard]) => {
    if (artboard.width % slideCount !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "stripNotDivisible",
        path: ["artboards", artboardID, "width"],
      });
    }
    if (
      !isStripWithinLimits({ width: artboard.width, height: artboard.height })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "stripTooLarge",
        path: ["artboards", artboardID],
      });
    }
  });
};

export const creativeTemplateDocSchema = z
  .object({
    schemaVersion: z.string().min(1),
    id: z.string().min(1),
    advertiserID: z.string().min(1),
    name: z.string(),
    status: z.enum(["draft", "approved", "archived"]),
    editor: z.object({
      referenceImageAssetID: z.string().min(1).nullish(),
      activeArtboardID: z.string().min(1),
      activeSlideID: z.string().min(1).optional(),
      analysis: z
        .object({
          status: z.enum(["processing", "success", "error"]),
          jobID: z.string().min(1).nullish(),
          errorCode: z.string().nullish(),
        })
        .optional(),
      analysisBySlide: z
        .record(
          z.object({
            status: z.enum(["processing", "success", "error"]),
            jobID: z.string().min(1).nullish(),
            errorCode: z.string().nullish(),
          })
        )
        .optional(),
    }),
    layoutKind: z
      .enum([
        CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE,
        CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL,
        CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL,
      ])
      .optional(),
    layoutConfigurations: z
      .object({
        continuousCarousel: z
          .object({ slideCount: z.number().int() })
          .optional(),
      })
      .optional(),
    slides: z.array(slideSchema).optional(),
    assets: z
      .record(z.union([backdropAssetSchema, composeAssetSchema]))
      .default({}),
    variables: z.record(variableSchema).default({}),
    nodes: z.record(nodeSchema).default({}),
    artboards: artboardMapSchema.default({}),
    audit: z.object({
      createdBy: z.string().optional(),
      creationTime: z.string().optional(),
      updateTime: z.string().optional(),
    }),
  })
  .superRefine((doc, ctx) => {
    const sharedNodes = doc.nodes || {};
    const artboards = doc.artboards || {};
    const activeArtboardID = doc.editor?.activeArtboardID;
    const layoutKind = doc.layoutKind;
    const slides = doc.slides;

    if (layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL) {
      const slideList = Array.isArray(slides) ? slides : [];
      if (
        slideList.length < CREATIVE_TEMPLATE_SLIDE_MIN ||
        slideList.length > CREATIVE_TEMPLATE_SLIDE_MAX
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "slideBounds",
          path: ["slides"],
        });
      }
      const ids = slideList.map((slide) => slide?.id).filter(Boolean);
      if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "duplicateSlideId",
          path: ["slides"],
        });
      }
      const activeSlideID = doc.editor?.activeSlideID;
      if (activeSlideID && !ids.includes(activeSlideID)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "unknownActiveSlide",
          path: ["editor", "activeSlideID"],
        });
      }
      const expectedKeys = Object.keys(slideList[0]?.artboards || {}).sort();
      slideList.forEach((slide, slideIndex) => {
        const slideKeys = Object.keys(slide?.artboards || {}).sort();
        if (slideKeys.join("\0") !== expectedKeys.join("\0")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "misalignedSlideFormats",
            path: ["slides", slideIndex, "artboards"],
          });
        }
        refineArtboardMap({
          artboards: slide?.artboards || {},
          sharedNodes: {},
          assets: doc.assets,
          ctx,
          path: ["slides", slideIndex, "artboards"],
        });
      });
      const activeSlide = slideList.find(
        (slide) => slide?.id === activeSlideID
      );
      const slideArtboards =
        activeSlide?.artboards || slideList[0]?.artboards || {};
      if (activeArtboardID && !slideArtboards[activeArtboardID]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "unknownActiveArtboard",
          path: ["editor", "activeArtboardID"],
        });
      }
    } else if (Array.isArray(slides) && slides.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "slidesNotAllowed",
        path: ["slides"],
      });
    } else {
      if (Object.keys(artboards).length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "artboardRequired",
          path: ["artboards"],
        });
      }
      if (activeArtboardID && !artboards[activeArtboardID]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "unknownActiveArtboard",
          path: ["editor", "activeArtboardID"],
        });
      }
      refineArtboardMap({
        artboards,
        sharedNodes,
        assets: doc.assets,
        ctx,
        path: ["artboards"],
      });
    }

    if (layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL) {
      refineContinuousStrips({ doc, ctx });
    }

    Object.entries(doc.assets || {}).forEach(([key, asset]) => {
      const isBackdropKey = key === "reference" || key.startsWith("reference_");
      if (!isBackdropKey && asset?.role === "editorBackdrop") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "backdropNotCompose",
          path: ["assets", key, "role"],
        });
      }
    });
  });

export const parseCreativeTemplateDoc = (doc) =>
  creativeTemplateDocSchema.safeParse(doc);
